import { CircularProgress } from '@material-ui/core';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import React from 'react';
import { ClaimStatus, ClaimType } from '../../interfaces/billing';
import { SelectOption } from '../../interfaces/common';
import { toSelectOptions } from '../../util/eligibility';

interface FiltersProps {
  claimType: string[];
  clinic: string[];
  clinics: SelectOption[];
  patient: string[];
  patientOptions: SelectOption[];
  status: string[];
  filterLoading: boolean;
  onClaimStatusChange: (event: SelectChangeEvent<string[]>) => void;
  onClaimTypeChange: (event: SelectChangeEvent<string[]>) => void;
  onClinicChange: (event: SelectChangeEvent<string[]>) => void;
  onFilter: () => void;
  onPatientChange: (value: SelectOption[]) => void;
}

export const Filters: React.FC<FiltersProps> = ({
  claimType,
  clinic,
  clinics,
  patient,
  patientOptions,
  status,
  filterLoading,
  onClaimStatusChange,
  onClaimTypeChange,
  onClinicChange,
  onFilter,
  onPatientChange,
}) => {
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel>Clinics</InputLabel>
            <Select
              value={clinic}
              renderValue={(selected) => selected.join(', ')}
              onChange={onClinicChange}
              label='Clinics'
              multiple>
              {clinics.map((clinicOption) => (
                <MenuItem key={clinicOption.value} value={clinicOption.value}>
                  <Checkbox
                    checked={clinic.includes(clinicOption.value)}
                    style={{ padding: '0 8px' }}
                    size='small'
                  />
                  <ListItemText
                    primary={clinicOption.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={onClaimStatusChange}
              label='Status'
              multiple
              renderValue={(selected) => selected.join(', ')}>
              {toSelectOptions(Object.values(ClaimStatus)).map((claimStatusOption) => (
                <MenuItem key={claimStatusOption.value} value={claimStatusOption.value}>
                  <Checkbox
                    checked={status.includes(claimStatusOption.value)}
                    style={{ padding: '0 8px' }}
                    size='small'
                  />
                  <ListItemText
                    primary={claimStatusOption.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel>Types</InputLabel>
            <Select
              value={claimType}
              onChange={onClaimTypeChange}
              label='Types'
              multiple
              renderValue={(selected) => selected.join(', ')}>
              {toSelectOptions(Object.values(ClaimType)).map((claimTypeOption) => (
                <MenuItem key={claimTypeOption.value} value={claimTypeOption.value}>
                  <Checkbox
                    checked={claimType.includes(claimTypeOption.value)}
                    style={{ padding: '0 8px' }}
                    size='small'
                  />
                  <ListItemText
                    primary={claimTypeOption.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Autocomplete
            size='small'
            multiple
            options={patientOptions}
            getOptionLabel={(option) => option.label}
            value={patientOptions.filter((option) => patient.includes(option.value))}
            onChange={(_, value) => onPatientChange(value)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={patientOptions.length === 0}
            noOptionsText='Load claims to filter by patient'
            renderInput={(params) => (
              <TextField {...params} variant='outlined' label='Patients' />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            size='large'
            fullWidth
            color='primary'
            variant='contained'
            disabled={filterLoading}
            startIcon={
              filterLoading ? <CircularProgress style={{ color: '#ffffff' }} size={20} /> : null
            }
            onClick={onFilter}>
            Apply filter
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
